import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, NoticeBox, Tags } from "../../ui";
import { ConfirmModal } from "../../modals/Confirm";
import {
  assignBranchUserAPI,
  createBranchUserAPI,
  listBranchUsersAPI,
  listEligibleBranchUsersAPI,
  resendBranchUserInvitationAPI,
  unassignBranchUserAPI,
  updateBranchUserAPI,
} from "../../../utils/api/branchPreparation";
import { BRANCH_USERS_TEXT, LOGIN_DOMAIN, USERNAME_PATTERN } from "./branchUsersText";
import "./BranchUsersSection.css";

/*
  Branch Users of one branch: the `role: branch` accounts that sign in to LUD and see only the branches assigned to them.
  * Select Existing User - a searchable list of branch users connected to the stores the client manages (the server decides who is listed);
    the same account can be assigned to branches of several stores.
  * Create New User - name, LUD sign-in username (the domain @lud.net is fixed), a real contact email and an optional phone. The server
    checks both emails for uniqueness, creates the account, assigns it and e-mails the single-use password-setup invitation.
  * Remove Assignment - removes this branch only; the account and its other assignments stay.
  * Edit - name / phone of an account that belongs only to the client's stores.
  Preparing users needs no subscription and activates nothing.
*/

const tokenOf = () => localStorage.getItem("auth-token");

export default function BranchUsersSection({ lang = "ar", storeId, branch, onChanged }) {
  const t = BRANCH_USERS_TEXT[lang === "en" ? "en" : "ar"];
  const ar = lang !== "en";
  const branchName = branch?.name || "";
  const [state, setState] = useState("loading"); // loading | ready | error
  const [users, setUsers] = useState([]);
  const [notice, setNotice] = useState(null); // { kind, text }
  const [mode, setMode] = useState(null); // null | "select" | "create"
  const [removing, setRemoving] = useState(null);
  const [editing, setEditing] = useState(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const messageFor = useCallback((error) => t.errors[error?.code] || error?.message || t.errorFallback, [t]);

  const load = useCallback(async () => {
    if (!storeId || !branch?.id) return;
    try {
      const list = await listBranchUsersAPI(tokenOf(), storeId, branch.id);
      if (!alive.current) return;
      setUsers(list);
      setState("ready");
    } catch (error) {
      if (!alive.current) return;
      setNotice({ kind: "error", text: messageFor(error) });
      setState("error");
    }
  }, [storeId, branch?.id, messageFor]);

  useEffect(() => {
    setState("loading");
    setMode(null);
    setNotice(null);
    load();
  }, [load]);

  const changed = async (text, kind = "success") => {
    setNotice({ kind, text });
    await load();
    await onChanged?.();
  };

  const remove = async () => {
    const target = removing;
    setRemoving(null);
    try {
      await unassignBranchUserAPI(tokenOf(), storeId, branch.id, target.id);
      await changed(t.removedOk);
    } catch (error) {
      setNotice({ kind: "error", text: messageFor(error) });
    }
  };

  const resend = async (user) => {
    try {
      const result = await resendBranchUserInvitationAPI(tokenOf(), storeId, branch.id, user.id);
      setNotice(result.invitation === "failed" ? { kind: "error", text: t.invitationFailed } : { kind: "success", text: t.invitationSent });
    } catch (error) {
      setNotice({ kind: "error", text: messageFor(error) });
    }
  };

  return (
    <div className="branch-users" dir={ar ? "rtl" : "ltr"} data-testid="branch-users">
      <p className="branch-users-hint">{t.hint}</p>

      {notice && (
        <NoticeBox variant={notice.kind === "success" ? "success" : "danger"} size="sm">
          <span role={notice.kind === "error" ? "alert" : "status"}>{notice.text}</span>
        </NoticeBox>
      )}

      {state === "loading" && <p role="status">{t.loading}</p>}
      {state === "error" && <Button type="button" size="sm" appearance="soft" onClick={load}>{t.retry}</Button>}
      {state === "ready" && users.length === 0 && <p className="branch-users-empty">{t.none}</p>}

      {users.length > 0 && (
        <ul className="branch-users-list">
          {users.map((user) => (
            <li key={user.id}>
              {editing === user.id ? (
                <EditUser t={t} user={user} onCancel={() => setEditing(null)} onSaved={async () => { setEditing(null); await changed(t.updatedOk); }} onError={(error) => setNotice({ kind: "error", text: messageFor(error) })} storeId={storeId} branchId={branch.id} />
              ) : (
                <>
                  <div className="branch-users-main">
                    <strong dir="auto">{user.name}</strong>
                    <span dir="ltr">{user.loginEmail}</span>
                    {user.contactEmail && <small dir="ltr">{t.contact}: {user.contactEmail}</small>}
                    <div className="branch-users-tags">
                      {user.invitationPending && <Tags variant="warning" appearance="soft" size="sm">{t.invitationPending}</Tags>}
                      {user.disabled && <Tags variant="danger" appearance="soft" size="sm">{t.disabled}</Tags>}
                      {user.shared && <Tags variant="neutral" appearance="soft" size="sm">{t.shared}</Tags>}
                    </div>
                    {user.assignments.length > 1 && (
                      <small>
                        {t.otherBranches}: {user.assignments.map((item) => `${item.storeName} / ${item.branchName}`).join("، ")}
                      </small>
                    )}
                  </div>
                  <div className="branch-users-actions">
                    {user.editable && <Button type="button" size="sm" appearance="outline" onClick={() => setEditing(user.id)}>{t.edit}</Button>}
                    {user.invitationPending && !user.disabled && <Button type="button" size="sm" appearance="outline" onClick={() => resend(user)}>{t.resend}</Button>}
                    <Button type="button" size="sm" variant="danger" appearance="outline" onClick={() => setRemoving(user)}>{t.remove}</Button>
                  </div>
                  {user.shared && <small className="branch-users-hint">{t.sharedEditHint}</small>}
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="branch-users-toolbar">
        <Button type="button" size="sm" appearance={mode === "select" ? "filled" : "soft"} onClick={() => setMode(mode === "select" ? null : "select")}>{t.selectExisting}</Button>
        <Button type="button" size="sm" appearance={mode === "create" ? "filled" : "soft"} onClick={() => setMode(mode === "create" ? null : "create")}>{t.createNew}</Button>
      </div>

      {mode === "select" && (
        <SelectExisting
          t={t}
          assignedIds={users.map((user) => user.id)}
          onAssign={async (user) => {
            try {
              await assignBranchUserAPI(tokenOf(), storeId, branch.id, user.id);
              await changed(t.assignedOk);
            } catch (error) {
              setNotice({ kind: "error", text: messageFor(error) });
            }
          }}
        />
      )}
      {mode === "create" && (
        <CreateUser
          t={t}
          onCreate={async (input) => {
            const result = await createBranchUserAPI(tokenOf(), storeId, branch.id, input);
            setMode(null);
            await changed(!result.created ? t.reused : result.invitation === "accepted" ? t.createdInvited : result.invitation === "failed" ? t.createdNotInvited : t.created, result.invitation === "failed" ? "error" : "success");
          }}
          messageFor={messageFor}
        />
      )}

      {removing && (
        <ConfirmModal title={t.removeTitle} message={t.removeMessage(removing.name, branchName)} onConfirm={remove} onCancel={() => setRemoving(null)} />
      )}
    </div>
  );
}

function SelectExisting({ t, assignedIds, onAssign }) {
  const [search, setSearch] = useState("");
  const [result, setResult] = useState({ state: "loading", users: [] });
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setResult((current) => ({ ...current, state: "loading" }));
      try {
        const users = await listEligibleBranchUsersAPI(tokenOf(), search.trim().length >= 2 ? search.trim() : "");
        if (!cancelled) setResult({ state: "ready", users });
      } catch {
        if (!cancelled) setResult({ state: "error", users: [] });
      }
    }, search ? 250 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, assignedIds.length]);
  return (
    <div className="branch-users-panel">
      <Input label={t.search} value={search} onChange={(event) => setSearch(event.target.value)} />
      <small>{t.searchHint}</small>
      {result.state === "ready" && result.users.length === 0 && <p>{t.noResults}</p>}
      {result.state === "error" && <p role="alert">{t.loadError}</p>}
      <ul className="branch-users-list" aria-label={t.selectExisting}>
        {result.users.map((user) => {
          const already = assignedIds.includes(user.id);
          return (
            <li key={user.id}>
              <div className="branch-users-main">
                <strong dir="auto">{user.name}</strong>
                <span dir="ltr">{user.loginEmail}</span>
                {user.contactEmail && <small dir="ltr">{t.contact}: {user.contactEmail}</small>}
                {user.assignments.length > 0 && <small>{t.otherBranches}: {user.assignments.map((item) => `${item.storeName} / ${item.branchName}`).join("، ")}</small>}
                {user.disabled && <Tags variant="danger" appearance="soft" size="sm">{t.disabled}</Tags>}
              </div>
              <div className="branch-users-actions">
                <Button type="button" size="sm" disabled={already || user.disabled} onClick={() => onAssign(user)}>{already ? t.assigned : t.assign}</Button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function CreateUser({ t, onCreate, messageFor }) {
  const [form, setForm] = useState({ name: "", username: "", contactEmail: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);
  const change = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const username = form.username.trim().toLowerCase();

  const submit = async () => {
    const found = {};
    if (!form.name.trim()) found.name = t.required;
    if (!USERNAME_PATTERN.test(username) || username.includes("..")) found.username = t.badUsername;
    const contact = form.contactEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(contact) || contact.endsWith(`@${LOGIN_DOMAIN}`)) found.contactEmail = t.badEmail;
    if (form.phone.trim() && !/^\+?[0-9\s()-]{6,20}$/.test(form.phone.trim())) found.phone = t.badPhone;
    setErrors(found);
    setFormError("");
    if (Object.keys(found).length) return;
    setBusy(true);
    try {
      await onCreate({ name: form.name.trim(), username, contactEmail: contact, ...(form.phone.trim() ? { phone: form.phone.trim() } : {}) });
    } catch (error) {
      if (error?.code === "LOGIN_EMAIL_TAKEN" || error?.code === "INVALID_USERNAME" || error?.code === "USERNAME_RESERVED") setErrors({ username: messageFor(error) });
      else if (error?.code === "CONTACT_EMAIL_TAKEN" || error?.code === "INVALID_CONTACT_EMAIL") setErrors({ contactEmail: messageFor(error) });
      else setFormError(messageFor(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="branch-users-panel">
      <Input label={t.name} value={form.name} error={errors.name} onChange={change("name")} />
      <div className="branch-users-login">
        <Input label={t.username} dir="ltr" placeholder={t.usernamePlaceholder} value={form.username} error={errors.username} onChange={change("username")} />
        <span className="branch-users-domain" dir="ltr" aria-hidden="true">@{LOGIN_DOMAIN}</span>
      </div>
      <small>{t.usernameHint}</small>
      <p className="branch-users-preview" dir="ltr">
        {t.loginPreview}: <strong data-testid="login-preview">{`${username || "username"}@${LOGIN_DOMAIN}`}</strong>
      </p>
      <Input label={t.contactEmail} type="email" dir="ltr" value={form.contactEmail} error={errors.contactEmail} onChange={change("contactEmail")} />
      <small>{t.contactHint}</small>
      <Input label={t.phone} type="tel" dir="ltr" value={form.phone} error={errors.phone} onChange={change("phone")} />
      {formError && <p role="alert" className="branch-users-error">{formError}</p>}
      <div className="branch-users-actions">
        <Button type="button" disabled={busy} onClick={submit}>{busy ? t.saving : t.createNew}</Button>
      </div>
    </div>
  );
}

function EditUser({ t, user, storeId, branchId, onCancel, onSaved, onError }) {
  const [form, setForm] = useState({ name: user.name || "", phone: user.phone || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await updateBranchUserAPI(tokenOf(), storeId, branchId, user.id, { name: form.name.trim(), phone: form.phone.trim() });
      await onSaved();
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="branch-users-panel">
      <Input label={t.name} value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
      <Input label={t.phone} type="tel" dir="ltr" value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} />
      <div className="branch-users-actions">
        <Button type="button" disabled={busy} onClick={save}>{busy ? t.saving : t.save}</Button>
        <Button type="button" appearance="outline" disabled={busy} onClick={onCancel}>{t.cancel}</Button>
      </div>
    </div>
  );
}
